import { createApi } from '@reduxjs/toolkit/dist/query/react';

import {
  getBaseQuery,
  getListProvidesTags,
  getUrlParamsString,
  TransformedErrorResponseWithNotification,
  TransformedResponseWithNotification,
} from '../generic';
import * as endpoints from '../endpoints';
import { REDUX_CACHE, USERS, HTTP_METHOD, ACL } from '../constants';
import { IUser } from '../../../types/interfaces/user';
import { ICommunity } from '../../../types/interfaces/community';
import { ISubscriptionRestriction } from '../../../types/interfaces/subscription-restriction';
import { INewFile } from '../../../types/interfaces/new-file';
import { IAppFile } from '../../../types/interfaces/app-file';
import { IAcl } from '../../../types/interfaces/acl';
import { IAccessResponse } from '../../../types/interfaces/access-response';

export const usersApi = createApi({
  reducerPath: 'usersApi',
  tagTypes: [
    USERS.USERS,
    USERS.ADMIN_USERS,
    USERS.PERMISSIONS,
    USERS.SELECTED_USER,
    USERS.ME,
    USERS.USER_COMMUNITIES,
    USERS.USER_SUBSCRIBERS,
    USERS.USER_SUBSCRIPTIONS,
    USERS.RESTRICTIONS,
    USERS.USER_SUBSCRIBER_REQUESTS,
    USERS.USER_SUBSCRIPTION_REQUESTS,
    USERS.REJECTED_USER_SUBSCRIBER_REQUESTS,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getUsersApi: builder.query<
      IUser[],
      { searchValue: string; pageSize: number; page: number }
    >({
      keepUnusedDataFor: 3600,
      query({ searchValue, pageSize, page }) {
        const url =
          endpoints.User.ROOT +
          '?' +
          getUrlParamsString({
            searchValue: searchValue,
            pageSize: pageSize,
            page: page,
          });

        return {
          url,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => getListProvidesTags(result, USERS.USERS),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${JSON.stringify(queryArgs)}`;
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    getMyDetailsApi: builder.query<IUser, void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.User.ME,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => [{ type: USERS.ME }],
      ...TransformedErrorResponseWithNotification(),
    }),
    getMyAccessApi: builder.query<IAccessResponse, void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.User.ACCESS,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => [{ type: USERS.PERMISSIONS }],
      ...TransformedErrorResponseWithNotification(),
    }),
    getUserDetailsApi: builder.query<IUser, { id: string }>({
      keepUnusedDataFor: 3600,
      query({ id }) {
        return {
          url: `${endpoints.User.ROOT}/${id}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => [{ type: USERS.SELECTED_USER }],
      ...TransformedErrorResponseWithNotification(),
    }),
    getUserCommunitiesApi: builder.query<ICommunity[], string>({
      keepUnusedDataFor: 3600,
      query(userId) {
        return {
          url: endpoints.User.COMMUNITIES.replace('${1}', userId),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, USERS.USER_COMMUNITIES),
      ...TransformedErrorResponseWithNotification(),
    }),
    getUserSubscribersApi: builder.query<IUser[], string>({
      keepUnusedDataFor: 3600,
      query(userId) {
        return {
          url: endpoints.User.SUBSCRIBERS.replace('${1}', userId),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, USERS.USER_SUBSCRIBERS),
    }),
    getUserSubscriptionsApi: builder.query<IUser[], string>({
      keepUnusedDataFor: 3600,
      query(userId) {
        return {
          url: endpoints.User.SUBSCRIPTIONS.replace('${1}', userId),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, USERS.USER_SUBSCRIPTIONS),
      ...TransformedErrorResponseWithNotification(),
    }),
    getUserSubscriberRequestsApi: builder.query<IUser[], string>({
      keepUnusedDataFor: 3600,
      query(userId) {
        return {
          url: endpoints.User.SUBSCRIBER_REQUESTS.replace('${1}', userId),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, USERS.USER_SUBSCRIBER_REQUESTS),
    }),

    getRejectedUserSubscriberRequestsApi: builder.query<IUser[], string>({
      keepUnusedDataFor: 3600,
      query(userId) {
        return {
          url: endpoints.User.REJECTED_SUBSCRIBER_REQUESTS.replace(
            '${1}',
            userId,
          ),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, USERS.REJECTED_USER_SUBSCRIBER_REQUESTS),
    }),
    getUserSubscriptionRequestsApi: builder.query<IUser[], string>({
      keepUnusedDataFor: 3600,
      query(userId) {
        return {
          url: endpoints.User.SUBSCRIPTION_REQUESTS.replace('${1}', userId),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, USERS.USER_SUBSCRIPTION_REQUESTS),
      ...TransformedErrorResponseWithNotification(),
    }),
    updateUserApi: builder.mutation<
      void,
      { id: string; body: Partial<IUser> & { avatar?: IAppFile } }
    >({
      query({ id, body }) {
        return {
          url: `${endpoints.User.ROOT}/${id}`,
          method: HTTP_METHOD.PUT,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: USERS.USERS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: USERS.SELECTED_USER,
            },
            {
              type: USERS.ME,
            },
            {
              type: USERS.USERS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('User'),
    }),
    subscribeToUserApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: endpoints.User.SUBSCRIBE.replace('${1}', id),
          method: HTTP_METHOD.POST,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: USERS.USERS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: USERS.SELECTED_USER,
            },
            {
              type: USERS.USER_SUBSCRIPTIONS,
            },
            {
              type: USERS.USER_SUBSCRIBERS,
            },
            {
              type: USERS.ME,
            },
            {
              type: USERS.USER_SUBSCRIBER_REQUESTS,
            },
            {
              type: USERS.USER_SUBSCRIPTION_REQUESTS,
            },
            {
              type: USERS.REJECTED_USER_SUBSCRIBER_REQUESTS,
            },
            {
              type: USERS.USERS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    unsubscribeFromUserApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: endpoints.User.UNSUBSCRIBE.replace('${1}', id),
          method: HTTP_METHOD.POST,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: USERS.USERS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: USERS.ME,
            },
            {
              type: USERS.USER_SUBSCRIPTIONS,
            },
            {
              type: USERS.USER_SUBSCRIBERS,
            },
            {
              type: USERS.SELECTED_USER,
            },
            {
              type: USERS.USERS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    changeUserSubscriptionStatusApi: builder.mutation<
      void,
      { id: string; body: { status: string } }
    >({
      // todo add statuses
      query({ id, body }) {
        return {
          url: endpoints.User.STATUS.replace('${1}', id),
          method: HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: USERS.USERS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: USERS.USER_SUBSCRIBERS,
            },
            {
              type: USERS.SELECTED_USER,
            },
            {
              type: USERS.ME,
            },
            {
              type: USERS.USERS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    deleteUserApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${endpoints.User.ROOT}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: USERS.USERS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('User'),
    }),
    getRestrictionsApi: builder.query<ISubscriptionRestriction[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.User.RESTRICTIONS,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => getListProvidesTags(result, USERS.RESTRICTIONS),
      ...TransformedErrorResponseWithNotification(),
    }),
    getAdminUsersApi: builder.query<IUser[], void>({
      keepUnusedDataFor: 3600,
      query() {
        const url = endpoints.AdminUser.ROOT;

        return {
          url,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => getListProvidesTags(result, USERS.ADMIN_USERS),
      ...TransformedErrorResponseWithNotification(),
    }),
    postPutAdminUserApi: builder.mutation<void, { id: string; body: IAcl }>({
      query({ id, body }) {
        const url = endpoints.AdminUser.ROOT + (id ? `/${id}` : '');
        return {
          url,
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: USERS.ADMIN_USERS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: USERS.ADMIN_USERS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification(),
    }),
    deleteAdminUserApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${endpoints.AdminUser.ROOT}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: USERS.ADMIN_USERS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification(),
    }),
  }),
});

export const resetUserApiState = () => usersApi.util.resetApiState();

export const {
  useGetUsersApiQuery: useGetUsers,
  usePostPutAdminUserApiMutation: usePostPutAdminUser,
  useDeleteAdminUserApiMutation: useDeleteAdminUser,
  useGetAdminUsersApiQuery: useGetAdminUsers,
  useLazyGetUsersApiQuery: useLazyGetUsers,
  useGetUserDetailsApiQuery: useGetUserDetails,
  useGetMyDetailsApiQuery: useGetMyDetails,
  useGetMyAccessApiQuery: useGetMyAccess,
  useGetUserCommunitiesApiQuery: useGetUserCommunities,
  useGetUserSubscribersApiQuery: useGetUserSubscribers,
  useLazyGetUserSubscribersApiQuery: useLazyGetUserSubscribers,
  useGetUserSubscriptionsApiQuery: useGetUserSubscriptions,
  useLazyGetUserSubscriptionsApiQuery: useLazyGetUserSubscriptions,
  useLazyGetMyDetailsApiQuery: useLazyGetMyDetails,
  useLazyGetUserDetailsApiQuery: useLazyGetUserDetails,
  useUpdateUserApiMutation: useUpdateUser,
  useSubscribeToUserApiMutation: useSubscribeToUser,
  useUnsubscribeFromUserApiMutation: useUnsubscribeFromUser,
  useChangeUserSubscriptionStatusApiMutation: useChangeUserSubscriptionStatus,
  useDeleteUserApiMutation: useDeleteUser,
  useGetRestrictionsApiQuery: useGetUserRestrictions,
  useGetUserSubscriberRequestsApiQuery: useGetUserSubscriberRequests,
  useGetRejectedUserSubscriberRequestsApiQuery:
    useGetRejectedUserSubscriberRequests,
  useGetUserSubscriptionRequestsApiQuery: useGetUserSubscriptionRequests,
  useLazyGetUserSubscriberRequestsApiQuery: useLazyGetUserSubscriberRequests,
  useLazyGetRejectedUserSubscriberRequestsApiQuery:
    useLazyGetRejectedUserSubscriberRequests,
  useLazyGetUserSubscriptionRequestsApiQuery:
    useLazyGetUserSubscriptionRequests,
} = usersApi;
